import Random from "rung/src/random";
import Glyphs from "../assets/objects/glyphs";
import Entity from "./entity";
import Speed from "./speed";
import Mass from "./mass";
import Health from "./health";
import Equipment from "./equipment";
import Role from "./role";
import Statblock from "./statblock";
import Transport from "./transport";
import { plotLine } from "flatland/src/line";
import { createPathfinder } from "./pathfinder";
import { Commands } from "./commands";
import * as actions from "./actions";

const rng = new Random(Math.random);

function logEvent(message) {
  document.dispatchEvent(new CustomEvent("game.event", {
    detail: {
      message,
    }
  }));
}

// TODO: should this be stored on the adventure game state rather than be a component?
class SocialModel {
  constructor() {
    this.meetings = new Set();
  }

  meeting(name) {
    this.meetings.add(name);
  }

  hasMet(name) {
    return this.meetings.has(name);
  }
}

class Player extends Entity {
  static create() {
    return new Player(null, null, Glyphs.Player, "Player", {
      mass: new Mass(Mass.Medium),
      speed: new Speed(10),
      health: new Health(24),
      stats: new Statblock(12, 12, 12),
      equipment: Equipment.blankSheet(),
      role: Role.startingClass(),
      transport: new Transport(),
      social: new SocialModel()
    });
  }

  constructor(x, y, glyph, name, components) {
    super(x, y, glyph, name, components);
    this.command = false;
    this.currentStory = false;
    this.autoExplore = false;
  }

  get epithet() {
    return `${this.equipment.quality} ${this.role.title}`;
  }

  isPlayer() {
    return true;
  }

  canTakeTurn() {
    return true;
  }

  isRangedWeaponEquipped() {
    return this.equipment.isRangedWeaponEquipped();
  }

  activateAutoExplore(stage, target) {
    // TODO: encapsulate this properly
    this.autoExplore = true;
    this.autoExplorePath = [];
    this.autoExploreCooldown = 0;

    const adjacentNodes = (x, y) => stage.adjacentPoints(x, y);
    const costHeuristic = (from, to) => (
      Math.abs(from.x - to.x) + Math.abs(from.y - to.y) * 1.01
    );
    const traversalCost = (x, y) => stage.movementCost(x, y, true);
    const isTraversable = (x, y) => stage.canMoveTo(x, y);
  
    const search = createPathfinder(
      adjacentNodes,
      costHeuristic,
      traversalCost,
      isTraversable
    );
  
    let unreachable = false;

    let path = search(
      { x: this.x, y: this.y },
      { x: target.x, y: target.y }
    );

    if (!path) {
      // Target is unreachable from the current player position so plot
      // a direct line to it and stop at the first blocking tile.
      unreachable = true;
      path = plotLine({x: this.x, y: this.y}, target);
      path.shift();
    }

    let prevStep = { x: this.x, y: this.y };

    for (const pathStep of path) {
      const mx = pathStep.x - prevStep.x;
      const my = pathStep.y - prevStep.y;
      prevStep = pathStep;

      if (unreachable) {
        if (this.transport.isSailing()) {
          if (mx == 0 || my == 0) {
            if (!stage.canSailTo(pathStep.x, pathStep.y)) break;
          } else {
            if (!stage.canSailTo(prevStep.x + mx, prevStep.y)) break;
            if (!stage.canSailTo(prevStep.x, prevStep.y + my)) break;
          }
        } else {
          if (mx == 0 || my == 0) {
            if (!stage.canWalkTo(pathStep.x, pathStep.y)) break;
          } else {
            if (!stage.canWalkTo(prevStep.x + mx, prevStep.y)) break;
            if (!stage.canWalkTo(prevStep.x, prevStep.y + my)) break;
          }
        }
      }

      if (mx < 0) {
        this.autoExplorePath.push(Commands.MoveLeft);
      } else if (mx > 0) {
        this.autoExplorePath.push(Commands.MoveRight);
      }

      if (my < 0) {
        this.autoExplorePath.push(Commands.MoveUp);
      } else if (my > 0) {
        this.autoExplorePath.push(Commands.MoveDown);
      }
    }

    if (this.autoExplorePath.length == 0) {
      this.autoExplore = false;
      this.command = Commands.Rest;
    } else {
      this.command = this.autoExplorePath.shift();
    }
  }

  needsInput() {
    if (this.autoExplore) {
      if (this.autoExploreCooldown <= 0) {
        this.autoExploreCooldown = 4;
        return false;
      } else {
        this.autoExploreCooldown--;
        return true;
      }
    }

    return this.command === false;
  }

  takeTurn(stage) {
    switch (this.command.action) {
      case "Rest":
        actions.restAction(this, this.command, stage);
      break;

      case "Move":
        actions.moveAction(this, this.command, stage);
      break;

      case "RangedAttack":
        actions.fireAction(this, this.command.detail.target, stage);
      break;

      case "Get":
        actions.getAction(this, this.command.detail, stage);
      break;

      case "Drop":
        actions.dropAction(this, this.command.detail, stage);
      break;

      case "Open":
        actions.openAction(this, this.command.detail, stage);
      break;

      case "Close":
        actions.closeAction(this, this.command.detail, stage);
      break;
    }

    if (this.autoExplore) {
      this.command = this.autoExplorePath.shift();

      if (this.autoExplorePath.length == 0) {
        this.autoExplore = false;
      }

      if (this.command === undefined) this.command = false;

    } else {
      this.command = false;
    }
    
    return !this.health.isDead();
  }

  pushCommand(command) {
    this.command = command;
  }

  pushStory(story) {
    this.currentStory = story;
  }

  popStory() {
    const story = this.currentStory;
    this.currentStory = false;
    return story;
  }
}

export default Player;
